import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type Modifier,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { useMemo, type ReactNode } from 'react';

import { SortableItem } from './subcomponents/SortableItem';

export interface SortableListProps<T> {
  /** The list to render and reorder. */
  items: T[];
  /** Extracts a stable, unique id from an item — must match dnd-kit's requirements. */
  getId: (item: T) => string | number;
  /** Called with the full list in its new order after a successful reorder. */
  onReorder: (items: T[]) => void;
  /** Renders one item's content — wrapped in a `SortableItem` automatically. */
  children: (item: T) => ReactNode;
  /**
   * dnd-kit modifiers that constrain a drag, such as keeping it on the
   * vertical axis and inside the list.
   */
  modifiers?: Modifier[];
  __private?: {
    /** Items it rejects keep their place but sit out of the sort: they cannot be dragged or dropped onto, and nothing shifts around them. */
    isSortable?: (item: T) => boolean;
  };
}

/**
 * Owns the list-level half of dnd-kit's sortable setup — `DndContext`,
 * `SortableContext`, and the reorder callback — so callers don't hand-wire
 * it per list. Wraps each item in a `SortableItem` automatically; render a
 * `SortableHandle` anywhere inside `children(item)` (e.g. as an `Accordion`
 * `leadingSlot`) to make that item draggable.
 *
 * `SortableList` renders no layout of its own around the items — wrap it in
 * a `Layout` (or similar) for spacing between rows.
 */
export function SortableList<T>({
  items,
  getId,
  onReorder,
  children,
  modifiers,
  __private,
}: SortableListProps<T>) {
  const isSortable = __private?.isSortable;
  const sortableItems = useMemo(
    () => (isSortable ? items.filter(isSortable) : items),
    [items, isSortable],
  );
  const ids = useMemo(() => sortableItems.map(getId), [sortableItems, getId]);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = ids.indexOf(active.id);
    const newIndex = ids.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const reordered = arrayMove(sortableItems, oldIndex, newIndex);
    let nextSortable = 0;
    onReorder(
      items.map((item) =>
        ids.includes(getId(item)) ? reordered[nextSortable++] : item,
      ),
    );
  };

  return (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={modifiers}
      sensors={sensors}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={ids}>
        {items.map((item) => (
          <SortableItem
            key={getId(item)}
            disabled={!ids.includes(getId(item))}
            id={getId(item)}
          >
            {children(item)}
          </SortableItem>
        ))}
      </SortableContext>
    </DndContext>
  );
}
